import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { buildLoginUrl } from "@/lib/redirect";

import { Button } from "@/components/ui/button";
import { Search, User } from "lucide-react";
import { CartIconButton } from "@/components/farmshop";
import { GlobalSearchOverlay } from "@/components/search/GlobalSearchOverlay";
import { useAuth } from "@/context/AuthContext";
import { useAccountMenuProps } from "@/hooks/useAccountMenuProps";
import { AccountMenu } from "@/components/nav/AccountMenu";
import { LocaleSwitcher } from "@/components/common/LocaleSwitcher";
import { useTranslate } from "@/i18n";

interface SiteHeaderProps {
  /** "compact" reduces visual weight for detail pages */
  density?: "default" | "compact";
}

const SiteHeader = ({ density = "default" }: SiteHeaderProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const accountProps = useAccountMenuProps();
  const isCompactDensity = density === "compact";
  const t = useTranslate();

  const [searchOpen, setSearchOpen] = useState(false);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <header className={`sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md ${isCompactDensity ? "border-border/30" : "border-border/50"}`}>
      <div className={`container flex items-center justify-between ${isCompactDensity ? "h-12" : "h-16"}`}>
        <Link to="/" className="flex items-center gap-2">
          <span className={`font-display font-medium text-foreground ${isCompactDensity ? "text-base" : "text-xl"}`}>
            Farmly
          </span>
        </Link>

        <nav className={`flex items-center ${isCompactDensity ? "gap-2.5 sm:gap-4" : "gap-4 sm:gap-6"}`}>
          <Button
            data-testid="search-open"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
            onClick={() => setSearchOpen(true)}
            title={`${t("nav.search")} (⌘K)`}
          >
            <Search className="h-4 w-4" />
            <span className="sr-only">{t("nav.search")}</span>
          </Button>

          <Link
            to="/om-oss"
            className="hidden sm:inline text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t("nav.about")}
          </Link>

          <LocaleSwitcher
            density={isCompactDensity ? "compact" : "default"}
            className="flex"
          />

          <CartIconButton showText />

          {user ? (
            <AccountMenu
              {...accountProps}
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
                  aria-label={t("nav.account")}
                >
                  <User className="h-4 w-4" />
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#B9B0A6]" />
                </Button>
              }
            />
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
              onClick={() => navigate(buildLoginUrl())}
              aria-label={t("nav.signIn")}
            >
              <User className="h-4 w-4" />
            </Button>
          )}
        </nav>
      </div>
      <GlobalSearchOverlay open={searchOpen} onClose={closeSearch} />
    </header>
  );
};

export default SiteHeader;
